import constants from "../../../../appConstants";
import { MultiSelectFilter, StaticFilter, DateTimeFilter } from "../../../dataGrid/columnFilters";
import {
  GeoCodeRenderer,
  DotRenderer,
  SpecialNeedsValueGetter,
  SpecialNeedsRenderer,
} from "../../../dataGrid/cellRenderer";
import iconRenderer from "../../../common/iconRenderer"
import { formatDayWindow, getDefaultHours, renderWindow } from "../EditCustomer/utils";

class GridConfig {
  constructor(configuration) {
    const config = configuration || {};
    this.name = "GridConfiguration";
    this.config = GridConfig.getConfiguration();
  }
  static getConfiguration() {
    return GridConfig.getDefaultConfiguration();
  }

  static getPrefferedRoute = (params) => {
    return params.data.deliveryDetails?.route || "";
  };

  static getLiftGate = (params) => {
    return params.data.deliveryDetails.isLiftGateRequired ? "L" : "";
  }

  static getTrailerDropValue = (params) => {
    return params.data.deliveryDetails.isTrailerDrop ? "Yes" : "";
  }

  static getLocal = (params) => {
    return params.data.deliveryDetails.isLocal ? "Yes" : "";
  }

  static getSpecialNeedsOption(params) {
    return params.context.specialNeedsOptions;
  }

  static specialNeedsValueGetter(params) {
    return SpecialNeedsValueGetter({
      data: {
        customerComments: params.data?.deliveryDetails?.customerComments,
        isDelivery: true,
      },
    });
  }

  static getDefaultConfiguration() {
    return {
      title: constants.gridNames.CustomerGrid,
      keyColumn: "code",
      rowSelection: "multiple",
      rowDeselection: "true",
      enableFilters: false,
      enableSorting: false,
      defaultColDef: {
        lockPinned: true,
        sortable: true,
        filter: true,
        resizable: true,
      },
      columns: [
        {
          headerName: "",
          minWidth: constants.columnWidth.VIOLATION_DOT,
          maxWidth: constants.columnWidth.VIOLATION_DOT,
          field: "violation",
          pinned: "left",
          lockPosition: true,
          cellClass: ["gridCell-centerAlign", "gridCell-dot"],
          filter: false,
          cellRendererFramework: DotRenderer,
          isInvisibleInShowHide: true,
          gridTitle: constants.gridNames.CustomerGrid,
        },
        {
          headerName: "Acc #",
          headerTooltip: "Customer Account Number",
          field: "code",
          minWidth: constants.columnWidth.CUSTOMER_CODE,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          tooltipField: "code",
          lockPosition: true,
        },
        {
          headerName: "Customer Name",
          headerTooltip: "Customer Name",
          field: "name",
          minWidth: constants.columnWidth.CUSTOMER,
          lockPosition: true,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          tooltipField: "name",
        },
        {
          headerName: "Spl Needs",
          headerTooltip: "Special Needs",
          minWidth: constants.columnWidth.SPL_NEED,
          cellClass: "gridCell-leftAlign",
          valueGetter: GridConfig.specialNeedsValueGetter,
          filterFramework: StaticFilter,
          filterParams: {
            getStaticOptions: GridConfig.getSpecialNeedsOption,
          },
          cellRendererFramework: SpecialNeedsRenderer,
        },
        {
          headerName: "Address",
          headerTooltip: "Customer Address",
          field: "address",
          minWidth: constants.columnWidth.CUSTOMER_ADDRESS,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          tooltipField: "address",
        },
        {
          headerName: "City",
          headerTooltip: "City",
          field: "city",
          cellClass: "gridCell-leftAlign",
          minWidth: constants.columnWidth.CITY,
          filterFramework: MultiSelectFilter,
          tooltipField: "city",
        },
        {
          headerName: "State",
          headerTooltip: "State",
          field: "state",
          cellClass: "gridCell-leftAlign",
          minWidth: constants.columnWidth.STATE,
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Zip",
          headerTooltip: "Zip",
          field: "zip",
          cellClass: "gridCell-leftAlign",
          minWidth: constants.columnWidth.ZIP_CODE,
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Geocoding",
          headerTooltip: "Geocoding",
          field: "deliveryDetails.isGeoCoded",
          minWidth: constants.columnWidth.GEOCODING,
          cellClass: "gridCell-left,Align",
          editable: false,
          cellRendererFramework: GeoCodeRenderer,
          filterFramework: StaticFilter,
          filterParams: {
            staticOptions: Object.values(constants.geoCode),
            renderIcon: iconRenderer,
          },
        },
        {
          headerName: "Route",
          headerTooltip: "Route",
          valueGetter: GridConfig.getPrefferedRoute,
          minWidth: constants.columnWidth.ROUTES,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Max Equip Size",
          headerTooltip: "Max Equipment Size",
          field: "deliveryDetails.maxEquipmentSize",
          minWidth: constants.columnWidth.W_140,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Liftgate",
          headerTooltip: "Liftgate",
          field: "deliveryDetails.isLiftGateRequired",
          valueGetter: GridConfig.getLiftGate,
          minWidth: constants.columnWidth.ROUTES,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Trailer Drop",
          headerTooltip: "Trailer Drop",
          field: "deliveryDetails.isTrailerDrop",
          valueGetter: GridConfig.getTrailerDropValue,
          minWidth: constants.columnWidth.W_115,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Local Customer",
          headerTooltip: "Is Local Customer",
          field: "deliveryDetails.isLocal",
          valueGetter: GridConfig.getLocal,
          minWidth: constants.columnWidth.W_135,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Default Hours",
          headerTooltip: "Default Hours",
          valueGetter: getDefaultHours,
          minWidth: constants.columnWidth.W_140,
          cellClass: "gridCell-leftAlign",
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true,
          },
        },
        {
          headerName: "Monday",
          headerTooltip: "Monday",
          field: "deliveryDetails.receivingHours.monday",
          tooltipField: "deliveryDetails.receivingHours.monday",
          valueFormatter: formatDayWindow,
          minWidth: constants.columnWidth.W_140,
          cellClass: "gridCell-leftAlign",
          cellRendererFramework: renderWindow,
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true,
          },
        },
        {
          headerName: "Tuesday",
          headerTooltip: "Tuesday",
          field: "deliveryDetails.receivingHours.tuesday",
          tooltipField: "deliveryDetails.receivingHours.tuesday",
          valueFormatter: formatDayWindow,
          minWidth: constants.columnWidth.W_140,
          cellClass: "gridCell-leftAlign",
          cellRendererFramework: renderWindow,
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true,
          },
        },
        {
          headerName: "Wednesday",
          headerTooltip: "Wednesday",
          field: "deliveryDetails.receivingHours.wednesday",
          tooltipField: "deliveryDetails.receivingHours.wednesday",
          valueFormatter: formatDayWindow,
          minWidth: constants.columnWidth.W_140,
          cellClass: "gridCell-leftAlign",
          cellRendererFramework: renderWindow,
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true,
          },
        },
        {
          headerName: "Thursday",
          headerTooltip: "Thursday",
          field: "deliveryDetails.receivingHours.thursday",
          tooltipField: "deliveryDetails.receivingHours.thursday",
          valueFormatter: formatDayWindow,
          minWidth: constants.columnWidth.W_140,
          cellClass: "gridCell-leftAlign",
          cellRendererFramework: renderWindow,
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true,
          },
        },
        {
          headerName: "Friday",
          headerTooltip: "Friday",
          field: "deliveryDetails.receivingHours.friday",
          tooltipField: "deliveryDetails.receivingHours.friday",
          valueFormatter: formatDayWindow,
          minWidth: constants.columnWidth.W_140,
          cellClass: "gridCell-leftAlign",
          cellRendererFramework: renderWindow,
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true,
          },
        },
        {
          headerName: "Arrive Early (mins)",
          headerTooltip: "Arrive Early (mins)",
          field: "deliveryDetails.appointmentBufferStart",
          minWidth: constants.columnWidth.W_150,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Arrive Late (mins)",
          headerTooltip: "Arrive Late (mins)",
          field: "deliveryDetails.appointmentBufferEnd",
          minWidth: constants.columnWidth.W_150,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
      ],
    };
  }
}

export default GridConfig;
